import React, { useState } from 'react';
import { FiAlertTriangle } from 'react-icons/fi';

const SafetyActionModal = ({ isOpen, onClose, onConfirm, report, isSubmitting }) => {
    const [action, setAction] = useState('delete_ban');
    const [reportingStatus, setReportingStatus] = useState('');
    const [agencyName, setAgencyName] = useState('');
    const [ncmecId, setNcmecId] = useState('');
    const [notes, setNotes] = useState('');
    const [banReason] = useState('Violating Child Safety Policy');

    if (!isOpen) return null;

    const handleSubmit = (e) => {
        e.preventDefault();

        // Validation
        if (!reportingStatus) {
            alert('Please select whether this was reported to authorities.');
            return;
        }
        if (reportingStatus === 'ncmec' && !ncmecId.trim()) {
            alert('Please enter the NCMEC Case ID.');
            return;
        }
        if (reportingStatus === 'other_agency' && !agencyName.trim()) {
            alert('Please enter the Agency Name.');
            return;
        }
        if (reportingStatus === 'not_csam' && notes.trim().length < 10) {
            alert('Please provide a detailed justification in the notes.');
            return;
        }

        onConfirm({
            action,
            reportingStatus,
            agencyName: reportingStatus === 'other_agency' ? agencyName : undefined,
            ncmecReportId: reportingStatus === 'ncmec' ? ncmecId : undefined,
            moderatorNotes: notes,
            banReason: action.includes('ban') || action.includes('suspend') ? banReason : undefined
        });
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} aria-hidden="true"></div>

                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg w-full">
                    <form onSubmit={handleSubmit}>
                        <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                            <div className="sm:flex sm:items-start">
                                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                    <FiAlertTriangle className="h-6 w-6 text-red-600" />
                                </div>
                                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                                    <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                                        Take Action: Child Safety
                                    </h3>
                                    <div className="mt-2">
                                        <p className="text-sm text-gray-500 mb-4">
                                            Child Safety reports require mandatory action and reporting. Evidence will be preserved automatically.
                                        </p>

                                        {/* Action Dropdown */}
                                        <div className="mb-4">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Action Required</label>
                                            <select
                                                value={action}
                                                onChange={(e) => setAction(e.target.value)}
                                                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-red-500 focus:border-red-500 sm:text-sm"
                                            >
                                                <option value="delete_ban">Delete Post & Ban User</option>
                                                <option value="delete_suspend">Delete Post & Suspend User</option>
                                                <option value="delete_only">Delete Post Only</option>
                                                <option value="escalate">Escalate to Legal (Do not delete yet)</option>
                                            </select>
                                        </div>

                                        {/* Reporting Status (New Required Field) */}
                                        <div className="mb-4 bg-gray-50 p-3 rounded-md border border-gray-200">
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Reported to Authorities? <span className="text-red-500">*</span>
                                            </label>

                                            <div className="space-y-3">
                                                {/* Option 1: NCMEC */}
                                                <div>
                                                    <div className="flex items-center">
                                                        <input
                                                            id="report_ncmec"
                                                            name="reportingStatus"
                                                            type="radio"
                                                            value="ncmec"
                                                            checked={reportingStatus === 'ncmec'}
                                                            onChange={(e) => setReportingStatus(e.target.value)}
                                                            className="focus:ring-red-500 h-4 w-4 text-red-600 border-gray-300"
                                                        />
                                                        <label htmlFor="report_ncmec" className="ml-3 block text-sm font-medium text-gray-700">
                                                            Yes, report filed with NCMEC
                                                        </label>
                                                    </div>
                                                    {reportingStatus === 'ncmec' && (
                                                        <div className="mt-2 ml-7">
                                                            <input
                                                                type="text"
                                                                value={ncmecId}
                                                                onChange={(e) => setNcmecId(e.target.value)}
                                                                placeholder="Enter NCMEC Case ID"
                                                                className="block w-full border border-gray-300 rounded-md shadow-sm py-1 px-2 focus:ring-red-500 focus:border-red-500 sm:text-sm"
                                                                required
                                                            />
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Option 2: Other Agency */}
                                                <div>
                                                    <div className="flex items-center">
                                                        <input
                                                            id="report_other"
                                                            name="reportingStatus"
                                                            type="radio"
                                                            value="other_agency"
                                                            checked={reportingStatus === 'other_agency'}
                                                            onChange={(e) => setReportingStatus(e.target.value)}
                                                            className="focus:ring-red-500 h-4 w-4 text-red-600 border-gray-300"
                                                        />
                                                        <label htmlFor="report_other" className="ml-3 block text-sm font-medium text-gray-700">
                                                            Yes, report filed with other agency
                                                        </label>
                                                    </div>
                                                    {reportingStatus === 'other_agency' && (
                                                        <div className="mt-2 ml-7">
                                                            <input
                                                                type="text"
                                                                value={agencyName}
                                                                onChange={(e) => setAgencyName(e.target.value)}
                                                                placeholder="Enter Agency Name (e.g. National Police)"
                                                                className="block w-full border border-gray-300 rounded-md shadow-sm py-1 px-2 focus:ring-red-500 focus:border-red-500 sm:text-sm"
                                                                required
                                                            />
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Option 3: Not CSAM */}
                                                <div>
                                                    <div className="flex items-center">
                                                        <input
                                                            id="report_none"
                                                            name="reportingStatus"
                                                            type="radio"
                                                            value="not_csam"
                                                            checked={reportingStatus === 'not_csam'}
                                                            onChange={(e) => setReportingStatus(e.target.value)}
                                                            className="focus:ring-red-500 h-4 w-4 text-red-600 border-gray-300"
                                                        />
                                                        <label htmlFor="report_none" className="ml-3 block text-sm font-medium text-gray-700">
                                                            No, not a CSAM case
                                                        </label>
                                                    </div>
                                                    {reportingStatus === 'not_csam' && (
                                                        <p className="mt-1 ml-7 text-xs text-red-600">
                                                            Detailed justification required in notes below.
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Moderator Notes */}
                                        <div className="mb-4">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Moderator Notes <span className="text-red-500">*</span>
                                            </label>
                                            <textarea
                                                required
                                                value={notes}
                                                onChange={(e) => setNotes(e.target.value)}
                                                rows={3}
                                                placeholder={reportingStatus === 'not_csam'
                                                    ? "EXPLAIN WHY THIS IS NOT CSAM. Be specific."
                                                    : "Describe the violation and action taken..."}
                                                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-red-500 focus:border-red-500 sm:text-sm"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-col sm:items-end">
                            {(reportingStatus === 'ncmec' || reportingStatus === 'other_agency') && (
                                <div className="mb-3 w-full bg-yellow-50 border-l-4 border-yellow-400 p-4">
                                    <div className="flex">
                                        <div className="flex-shrink-0">
                                            <FiAlertTriangle className="h-5 w-5 text-yellow-400" aria-hidden="true" />
                                        </div>
                                        <div className="ml-3">
                                            <p className="text-sm text-yellow-700">
                                                This action requires <strong>Supervisor Approval</strong>.
                                                The user will be suspended immediately and evidence preserved, but permanent deletion will wait for approval.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div className="sm:flex sm:flex-row-reverse w-full">
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm ${isSubmitting ? 'opacity-75 cursor-not-allowed' : ''}`}
                                >
                                    {isSubmitting ? 'Processing...' :
                                        (reportingStatus === 'ncmec' || reportingStatus === 'other_agency')
                                            ? 'Request Supervisor Approval'
                                            : 'Confirm Action'
                                    }
                                </button>
                                <button
                                    type="button"
                                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                    onClick={onClose}
                                    disabled={isSubmitting}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default SafetyActionModal;
